import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, CssFontSource, CustomFontSource, Appearance } from '@stripe/stripe-js';
import logEvent from 'analytics/logEvent';
import type { PublicRuntimeConfig } from 'app.types';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';

import { getCommunitySlug } from 'utils/community/getCommunitySlug';

import { getTariff } from 'domains/products/products.utils';

import PaymentButtons from 'components/complex/payments/PaymentForm/PaymentButtons';
import PaymentElements from 'components/complex/payments/PaymentForm/PaymentElements';
import SavedWallets from 'components/complex/payments/PaymentForm/SavedWallets';

import type { AppState } from 'redux-store';
import { useAppDispatch, useAppSelector } from 'redux-store';
import type { ProductType } from 'redux-store/slices/products/types';
import getPaymentsMethods from 'redux-store/thunks/paymentsMethods/getPaymentsMethods';
import { duration } from 'dayjs';

const { publicRuntimeConfig } = getConfig();
const { host, stripeKey }: PublicRuntimeConfig = publicRuntimeConfig;

interface Props {
  selectedPlan: ProductType;
}

export interface LogPaymentArgsType {
  paymentSystem: string | undefined;
}

const fonts: (CssFontSource | CustomFontSource)[] = [
  {
    family: 'November',
    weight: '400',
    src: `url(${host}fonts/NovemberLCG-Regular.otf)`,
  },
  {
    family: 'November',
    weight: '500',
    src: `url(${host}fonts/NovemberLCG-Medium.otf)`,
  },
  {
    family: 'November',
    weight: '700',
    src: `url(${host}fonts/NovemberLCG-Bold.otf)`,
  },
  {
    family: 'November',
    weight: '900',
    src: `url(${host}fonts/NovemberLCG-Heavy.otf)`,
  },
];

const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    fontFamily: 'November, sans-serif',
    fontSizeBase: '16px',
    fontSizeSm: '16px',
    colorText: '#B8BDC1',
  },
  rules: {
    '.Input': {
      color: 'black',
    },
  }
};

export default function PaymentForm({ selectedPlan }: Props): JSX.Element {
  const stripePromise = useMemo(
    () => loadStripe(stripeKey, { locale: 'en' }),
    [],
  );
  const discount = useAppSelector<
    AppState['products']['products']['0']['prices']['0']['discount']
  >(state => (state.products.products[0] || {}).prices[0].discount);
  const dispatch = useAppDispatch();
  const [selectedWalletId, setSelectedWalletId] = useState<null | string>(null);
  const router = useRouter();
  const coupon = router.query.coupon as string;

  const sourceQuery = (router.query.source as string) || 'unknown';
  const communityQuery = getCommunitySlug(
    router.query.community,
    router.asPath,
  );

  useEffect(() => {
    dispatch(getPaymentsMethods(null));
  }, [dispatch]);

  function logClickButton({ paymentSystem }: LogPaymentArgsType): void {
    logEvent('Web_Payment_Pay_Button_Clicked', {
      coupon,
      'coupon%': discount,
      tariff: getTariff(selectedPlan),
      duration: selectedPlan.prices[0].interval_count,
      plan_id: selectedPlan.id,
      payment_system: paymentSystem,
      pay_saved_card: paymentSystem === 'card' && selectedWalletId !== null,

      source: sourceQuery,
      community: communityQuery,
    });
  }

  return (
    <Elements stripe={stripePromise} options={{ fonts, appearance }}>
      <PaymentButtons
        coupon={coupon}
        selectedPlan={selectedPlan}
        logClickButton={logClickButton}
      />
      <SavedWallets
        selectedWalletId={selectedWalletId}
        setSelectedWalletId={setSelectedWalletId}
      />
      <PaymentElements
        coupon={coupon}
        selectedPlan={selectedPlan}
        selectedWalletId={selectedWalletId}
        setSelectedWalletId={setSelectedWalletId}
        logClickButton={logClickButton}
      />
    </Elements>
  );
}
